import { useState, useCallback, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import axios from "axios";
import { RichTextEditor } from "@/components/RichTextEditor";
import { useArtifactCollaboration } from "@/hooks/useArtifactCollaboration";
import {
  FileText, BookOpen, ClipboardCheck, GraduationCap, Pencil, Check, X,
  History, Undo2, Layers, MessageSquare, Sparkles, Users, Circle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

import { PIPELINE_API } from "./project-tabs/constants";

const API = PIPELINE_API;

const ARTIFACT_ICONS = {
  course_outline: BookOpen, lesson: FileText, assessment: ClipboardCheck,
  study_guide: GraduationCap, module: Layers, document: FileText,
};
const ARTIFACT_COLORS = {
  course_outline: "bg-emerald-50 text-emerald-700 border-emerald-200",
  lesson: "bg-blue-50 text-blue-700 border-blue-200",
  assessment: "bg-amber-50 text-amber-700 border-amber-200",
  study_guide: "bg-violet-50 text-violet-700 border-violet-200",
  module: "bg-rose-50 text-rose-700 border-rose-200",
  document: "bg-slate-50 text-slate-700 border-slate-200",
};
const STATUS_BADGE = {
  draft: "bg-amber-50 text-amber-600 border-amber-200",
  review: "bg-violet-50 text-violet-600 border-violet-200",
  approved: "bg-emerald-50 text-emerald-600 border-emerald-200",
  published: "bg-sky-50 text-sky-600 border-sky-200",
};
const CATEGORY_BADGE = {
  course: "bg-blue-50 text-blue-600", assessment: "bg-amber-50 text-amber-600",
  guide: "bg-violet-50 text-violet-600", report: "bg-rose-50 text-rose-600",
  plan: "bg-emerald-50 text-emerald-600", document: "bg-slate-50 text-slate-600",
};

/* ─── Text Selection Popover ─── */
const SelectionPopover = ({ position, text, onStartChat, onClose }) => {
  if (!position) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 4, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 4, scale: 0.95 }}
      className="fixed z-50 bg-white rounded-xl shadow-elevated border border-border/60 py-1.5 px-1.5 flex items-center gap-1"
      style={{ left: position.x, top: position.y - 44 }}
      data-testid="text-selection-popover"
    >
      <button onClick={() => onStartChat(text)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#014d7d] hover:bg-[#014d7d]/5" data-testid="selection-start-chat">
        <MessageSquare className="w-3 h-3" /> Chat about this
      </button>
      <button onClick={() => onStartChat(`Improve this text: "${text}"`)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-foreground/50 hover:bg-foreground/5" data-testid="selection-improve">
        <Sparkles className="w-3 h-3" /> Improve
      </button>
      <button onClick={onClose} className="w-6 h-6 flex items-center justify-center text-foreground/45 hover:text-foreground/50 rounded-lg"><X className="w-3 h-3" /></button>
    </motion.div>
  );
};

/* ─── Version History Panel ─── */
const VersionHistoryPanel = ({ artifactId, apiBase, onRevert, onClose }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewVersion, setPreviewVersion] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${apiBase}/artifacts/${artifactId}/versions`, { withCredentials: true });
        setVersions(res.data.versions || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [artifactId, apiBase]);

  return (
    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
      className="border-t border-border/40 bg-secondary/10" data-testid={`version-history-${artifactId}`}>
      <div className="px-5 py-3">
        <div className="flex items-center justify-between mb-3">
          <h5 className="text-xs font-medium text-foreground/70 flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Version History</h5>
          <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground" data-testid={`close-version-history-${artifactId}`}><X className="w-3.5 h-3.5" /></button>
        </div>
        {loading ? <p className="text-xs text-muted-foreground py-2">Loading versions...</p>
        : versions.length === 0 ? <p className="text-xs text-muted-foreground py-2">No version history yet</p>
        : (
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {[...versions].reverse()?.map((v, i) => (
              <div key={`v-${v.version}-${i}`}
                className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs cursor-pointer hover:bg-secondary/50 ${previewVersion?.version === v.version ? "bg-secondary/60 ring-1 ring-charcoal/10" : ""}`}
                onClick={() => setPreviewVersion(previewVersion?.version === v.version ? null : v)}
                data-testid={`version-item-${v.version}`}>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground/70">v{v.version}</span>
                  <span className="text-muted-foreground">{v.title}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground/60">{new Date(v.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                  <button onClick={(e) => { e.stopPropagation(); onRevert(v.version); }}
                    className="flex items-center gap-1 px-2 py-0.5 rounded bg-charcoal/5 hover:bg-charcoal/10 text-foreground/60 hover:text-foreground"
                    data-testid={`revert-to-v${v.version}`}>
                    <Undo2 className="w-3 h-3" /> Revert
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {previewVersion && (
          <div className="mt-3 p-3 rounded-lg bg-white border border-border/40 max-h-40 overflow-y-auto">
            <p className="text-[10px] text-muted-foreground mb-2 uppercase tracking-wider">Preview — v{previewVersion.version}</p>
            <div className="text-xs text-foreground/60 whitespace-pre-wrap line-clamp-6">
              {previewVersion.content?.slice(0, 400)}{previewVersion.content?.length > 400 && "..."}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

/* ─── Remote Cursors Overlay ─── */
const RemoteCursorsOverlay = ({ cursors }) => {
  const entries = Object.values(cursors);
  if (entries.length === 0) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-10" data-testid="remote-cursors-overlay">
      {entries?.map(c => (
        c.cursor && (
          <div
            key={c.user_id}
            className="absolute transition-all duration-150 ease-out"
            style={{ top: `${(c.cursor.from || 0) * 0.8}px`, left: `${Math.min((c.cursor.from || 0) % 80, 90)}%` }}
          >
            <div className="flex items-center gap-0" style={{ transform: "translateY(-100%)" }}>
              <div className="w-0.5 h-5 rounded-full" style={{ background: c.color }} />
              <span className="text-[9px] font-bold text-white px-1.5 py-0.5 rounded-md ml-0.5 whitespace-nowrap shadow-sm"
                style={{ background: c.color }}>
                {c.name}
              </span>
            </div>
          </div>
        )
      ))}
    </div>
  );
};

/* ─── Collaboration Presence Bar ─── */
const CollabBar = ({ users, isEditing }) => {
  const otherUsers = users?.filter(u => u.user_id); // all connected users
  if (otherUsers.length <= 1 && !isEditing) return null;

  return (
    <div className="flex items-center gap-2 px-5 py-2 border-t border-border/30 bg-[#f8f9fb]/60" data-testid="collab-bar">
      <div className="flex -space-x-1.5">
        {otherUsers.slice(0, 6)?.map((u, i) => (
          <div
            key={u.user_id || i}
            className="w-5 h-5 rounded-full border-2 border-white flex items-center justify-center text-white text-[6px] font-bold relative"
            style={{ background: u.color || "#014d7d" }}
            title={u.name}
            data-testid={`collab-user-${u.user_id || i}`}
          >
            {(u.name || "?").slice(0, 2).toUpperCase()}
            <span className="absolute -bottom-px -right-px w-1.5 h-1.5 bg-emerald-400 rounded-full border border-white" />
          </div>
        ))}
      </div>
      <span className="text-[10px] text-foreground/50 font-medium">
        {otherUsers.length} {otherUsers.length === 1 ? "editor" : "editors"} connected
      </span>
      {otherUsers.some(u => u.selection?.text) && (
        <span className="text-[10px] text-[#014d7d] font-semibold flex items-center gap-0.5">
          <Pencil className="w-2.5 h-2.5" /> Editing in progress
        </span>
      )}
    </div>
  );
};

/* ─── Artifact Card ─── */
const ArtifactCard = ({ artifact, onUpdate, onRevert, onStartChat, apiBase }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [editContent, setEditContent] = useState(artifact.content);
  const [editTitle, setEditTitle] = useState(artifact.title);
  const contentRef = useRef(null);
  const [selectionPos, setSelectionPos] = useState(null);
  const [selectedText, setSelectedText] = useState("");
  const Icon = ARTIFACT_ICONS[artifact.type] || FileText;
  const colorClass = ARTIFACT_COLORS[artifact.type] || ARTIFACT_COLORS.document;
  const statusClass = STATUS_BADGE[artifact.status] || STATUS_BADGE.draft;
  const catClass = CATEGORY_BADGE[artifact.category] || CATEGORY_BADGE.document;

  const { remoteUsers, remoteCursors, remoteContent, broadcastCursor, broadcastEdit } = useArtifactCollaboration(artifact.id, isEditing);

  // Apply remote content updates
  useEffect(() => {
    if (remoteContent && !isEditing) {
      // Another user saved — we're in read mode, just update view
      // The parent will refetch on save
    }
  }, [remoteContent, isEditing]);

  const handleSave = () => {
    onUpdate(artifact.id, editContent, editTitle);
    broadcastEdit(editContent);
    setIsEditing(false);
  };
  const handleCancel = () => {
    setEditContent(artifact.content);
    setEditTitle(artifact.title);
    setIsEditing(false);
  };
  const handleRevert = (version) => {
    onRevert(artifact.id, version);
    setShowVersions(false);
  };

  const handleEditorUpdate = (html) => {
    setEditContent(html);
    // Broadcast cursor position approximation
    const sel = window.getSelection();
    if (sel?.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      broadcastCursor(
        { from: range.startOffset, to: range.endOffset },
        sel.toString().length > 0 ? { from: range.startOffset, to: range.endOffset, text: sel.toString().slice(0, 50) } : null
      );
    }
  };

  const handleTextSelect = useCallback(() => {
    const sel = window.getSelection();
    const text = sel?.toString().trim();
    if (text && text.length > 3 && contentRef.current?.contains(sel?.anchorNode)) {
      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      setSelectedText(text);
      setSelectionPos({ x: rect.left + rect.width / 2 - 100, y: rect.top });
    } else {
      setSelectionPos(null);
      setSelectedText("");
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mouseup", handleTextSelect);
    return () => document.removeEventListener("mouseup", handleTextSelect);
  }, [handleTextSelect]);

  const otherEditors = remoteUsers?.filter(u => u.cursor || u.selection);

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="artifact-block border border-border/60 rounded-xl bg-white overflow-hidden"
      data-testid={`artifact-${artifact.id}`}>

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-border/40 bg-secondary/20">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center border shrink-0 ${colorClass}`}>
            <Icon className="w-3.5 h-3.5" />
          </div>
          {isEditing ? (
            <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
              className="text-sm font-medium bg-transparent border-b border-charcoal/30 outline-none px-1 py-0.5 flex-1"
              data-testid={`artifact-title-input-${artifact.id}`} />
          ) : (
            <h4 className="text-sm font-medium text-foreground/80 truncate">{artifact.title}</h4>
          )}
          <Badge variant="secondary" className="text-[10px] uppercase tracking-wider px-2 py-0 shrink-0">
            {artifact.type?.replace("_", " ")}
          </Badge>
          {artifact.category && <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-md ${catClass} shrink-0`}>{artifact.category}</span>}
          {artifact.status && <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${statusClass} shrink-0`}>{artifact.status}</span>}
          {artifact.version > 1 && <span className="text-[10px] text-muted-foreground shrink-0">v{artifact.version}</span>}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Collab presence avatars in header */}
          {remoteUsers.length > 0 && (
            <div className="flex -space-x-1 mr-1" data-testid={`artifact-collab-${artifact.id}`}>
              {remoteUsers.slice(0, 4)?.map((u, i) => (
                <div key={u.user_id || i}
                  className="w-5 h-5 rounded-full border-2 border-white flex items-center justify-center text-white text-[6px] font-bold relative group/cu"
                  style={{ background: u.color || "#014d7d" }}
                  title={u.name}>
                  {(u.name || "?").slice(0, 2).toUpperCase()}
                  <span className="absolute -bottom-px -right-px w-1.5 h-1.5 bg-emerald-400 rounded-full border border-white" />
                </div>
              ))}
              {remoteUsers.length > 4 && (
                <span className="text-[9px] text-foreground/50 ml-1">+{remoteUsers.length - 4}</span>
              )}
            </div>
          )}
          {isEditing ? (
            <>
              <button onClick={handleSave} className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center hover:bg-emerald-100" data-testid={`artifact-save-${artifact.id}`}><Check className="w-3.5 h-3.5" /></button>
              <button onClick={handleCancel} className="w-7 h-7 rounded-lg bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100" data-testid={`artifact-cancel-${artifact.id}`}><X className="w-3.5 h-3.5" /></button>
            </>
          ) : (
            <>
              <button onClick={() => setShowVersions(!showVersions)}
                className={`w-7 h-7 rounded-lg flex items-center justify-center ${showVersions ? "bg-charcoal/10 text-foreground" : "bg-secondary text-muted-foreground hover:bg-secondary/80 hover:text-foreground"}`}
                data-testid={`artifact-history-${artifact.id}`}><History className="w-3.5 h-3.5" /></button>
              <button onClick={() => setIsEditing(true)}
                className="w-7 h-7 rounded-lg bg-secondary text-muted-foreground flex items-center justify-center hover:bg-secondary/80 hover:text-foreground"
                data-testid={`artifact-edit-${artifact.id}`}><Pencil className="w-3.5 h-3.5" /></button>
            </>
          )}
        </div>
      </div>

      {/* Version history */}
      <AnimatePresence>
        {showVersions && <VersionHistoryPanel artifactId={artifact.id} apiBase={apiBase} onRevert={handleRevert} onClose={() => setShowVersions(false)} />}
      </AnimatePresence>

      {/* Content with remote cursors overlay */}
      <div className="relative px-5 py-4" ref={contentRef}>
        {isEditing && Object.keys(remoteCursors).length > 0 && (
          <RemoteCursorsOverlay cursors={remoteCursors} />
        )}
        {isEditing ? (
          <RichTextEditor content={editContent} onUpdate={handleEditorUpdate} placeholder="Edit artifact content..." />
        ) : (
          <div className="artifact-prose" data-testid={`artifact-rendered-${artifact.id}`}>
            <ReactMarkdown components={{
              h1: ({ children }) => <h1 className="font-heading font-semibold text-xl text-foreground/90 mt-6 mb-3 pb-2 border-b border-border/40 first:mt-0">{children}</h1>,
              h2: ({ children }) => <h2 className="font-heading font-medium text-lg text-foreground/85 mt-5 mb-2">{children}</h2>,
              h3: ({ children }) => <h3 className="font-heading font-medium text-base text-foreground/80 mt-4 mb-1.5">{children}</h3>,
              h4: ({ children }) => <h4 className="font-sans font-semibold text-sm text-foreground/75 mt-3 mb-1">{children}</h4>,
              p: ({ children }) => <p className="text-sm leading-relaxed text-foreground/70 mb-3">{children}</p>,
              ul: ({ children }) => <ul className="list-disc pl-5 mb-3 space-y-1 text-sm text-foreground/70">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal pl-5 mb-3 space-y-1 text-sm text-foreground/70">{children}</ol>,
              li: ({ children }) => <li className="leading-relaxed">{children}</li>,
              strong: ({ children }) => <strong className="font-semibold text-foreground/85">{children}</strong>,
              em: ({ children }) => <em className="italic text-foreground/65">{children}</em>,
              blockquote: ({ children }) => <blockquote className="border-l-3 border-clay/40 pl-4 py-1 my-3 bg-clay/5 rounded-r-lg text-sm text-foreground/65 italic">{children}</blockquote>,
              code: ({ inline, children }) => inline
                ? <code className="px-1.5 py-0.5 rounded bg-secondary text-xs font-mono text-foreground/75">{children}</code>
                : <pre className="bg-charcoal/[0.03] border border-border/40 rounded-lg p-4 mb-3 overflow-x-auto"><code className="text-xs font-mono text-foreground/70 leading-relaxed">{children}</code></pre>,
              pre: ({ children }) => <>{children}</>,
              hr: () => <hr className="my-4 border-border/40" />,
              table: ({ children }) => <div className="overflow-x-auto mb-3 border border-border/40 rounded-lg"><table className="w-full text-sm">{children}</table></div>,
              thead: ({ children }) => <thead className="bg-secondary/50 text-foreground/70">{children}</thead>,
              th: ({ children }) => <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide border-b border-border/40">{children}</th>,
              td: ({ children }) => <td className="px-3 py-2 text-foreground/65 border-b border-border/20">{children}</td>,
            }}>{artifact.content}</ReactMarkdown>
          </div>
        )}
      </div>

      {/* Collaboration presence bar */}
      <CollabBar users={remoteUsers} isEditing={isEditing} />

      {/* Selection popover */}
      <AnimatePresence>
        {selectionPos && selectedText && (
          <SelectionPopover
            position={selectionPos} text={selectedText}
            onStartChat={(text) => { onStartChat?.(text, artifact); setSelectionPos(null); setSelectedText(""); }}
            onClose={() => { setSelectionPos(null); setSelectedText(""); }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ─── Empty State ─── */
const EmptyState = ({ sessionGoal }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
    className="flex flex-col items-center justify-center h-full text-center px-8" data-testid="artifact-empty-state">
    <div className="w-16 h-16 rounded-2xl bg-secondary/60 flex items-center justify-center mb-5">
      <Layers className="w-7 h-7 text-muted-foreground/50" />
    </div>
    <h3 className="font-heading font-medium text-lg text-foreground/60 mb-2">Your artifacts will appear here</h3>
    <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
      As Kadal AI generates course outlines, lessons, assessments, and study guides, they will show up in this workspace for you to review and edit.
    </p>
  </motion.div>
);

/* ═══════════════ Main Component ═══════════════ */
export const ArtifactWorkspace = ({ artifacts, onUpdate, onRevert, onStartChat, sessionGoal, apiBase }) => {
  const categories = {};
  artifacts.forEach((a) => {
    const cat = a.category || a.type || "document";
    if (!categories[cat]) categories[cat] = { count: 0, statuses: {} };
    categories[cat].count++;
    const s = a.status || "draft";
    categories[cat].statuses[s] = (categories[cat].statuses[s] || 0) + 1;
  });

  return (
    <div className="h-full flex flex-col" data-testid="artifact-workspace">
      <div className="px-6 py-3 border-b border-border/40 flex items-center justify-between bg-white sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <h3 className="font-heading font-medium text-sm text-foreground/80">Artifacts</h3>
          {artifacts.length > 0 && <Badge variant="secondary" className="text-xs">{artifacts.length}</Badge>}
        </div>
        {Object.keys(categories).length > 0 && (
          <div className="flex items-center gap-1.5">
            {Object.entries(categories).slice(0, 4)?.map(([cat, info]) => (
              <span key={cat} className={`text-[9px] font-semibold px-2 py-0.5 rounded-md ${CATEGORY_BADGE[cat] || "bg-slate-50 text-slate-600"}`}>
                {cat}: {info.count}
              </span>
            ))}
          </div>
        )}
      </div>

      {artifacts.length === 0 ? (
        <EmptyState sessionGoal={sessionGoal} />
      ) : (
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <AnimatePresence mode="popLayout">
            {artifacts?.map((artifact) => (
              <ArtifactCard key={artifact.id} artifact={artifact} onUpdate={onUpdate} onRevert={onRevert} onStartChat={onStartChat} apiBase={apiBase} />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};
